import { IConfig } from "../../../lib/config";
import { FormConfiguration } from "../../shared/crud/form";
import { PropertyEntity } from "./property.entity";
import { v4 as uuidv4 } from "uuid";
import { messages, PropertyType, PropertyValuation } from "../messages";
import { translationFieldConfig } from "../../shared/widgets/translation";
import { HtmlEditorWidget } from "../../shared/widgets/html";
import SectionUnderline from "../../shared/widgets/sectionUnderline";
import { CountrySelector } from "../../shared/widgets/countrySelector";
import { GooglePlacesSelector } from "../../shared/widgets/googlePlacesSelector";
import { imageManagerConfig } from "../../shared/widgets/ImageManager";
import { PropertyType as PropertyTypeProto } from "../../../gen/witbooking/backoffice/property/v1/establishment_api_pb";
import { FieldProps } from "react-jsonschema-form";
import { PropertyConfigurationValue } from "../../../components/property-configuration-provider";

export function buildFormConfiguration(
  allowedLocales: string[],
  config: IConfig,
  formConfiguration: PropertyConfigurationValue
): FormConfiguration<PropertyEntity> {
  return {
    __fieldOrder: [
      "id",
      "ticker",
      "propertyType",
      "valuation",
      "translationsList",
      "locationSection",
      "country",
      "city",
      "address",
      "zipCode",
      "searchCoordinates",
      "latitude",
      "longitude",
      "timeZone",
      "contactSection",
      "phone",
      "adminEmail",
      "contactEmail",
      "multimediaList",
      "*",
    ],
    __useProtoStyleArrays: true,
    id: {
      default: () => uuidv4(),
      hidden: true,
    },
    ticker: {
      hidden: true,
    },
    propertyType: {
      messages: {
        label: messages.type,
      },
      default: () => PropertyTypeProto.HOTEL,
      enumLabels: [
        messages[PropertyType.HOTEL],
        messages[PropertyType.APART_HOTEL],
        messages[PropertyType.APARTMENT],
        messages[PropertyType.HOSTEL],
        messages[PropertyType.HOUSES],
        messages[PropertyType.HOTEL_WITH_SPA],
        messages[PropertyType.RURAL],
        messages[PropertyType.WATERING_PLACE],
        messages[PropertyType.WELLNESS],
        messages[PropertyType.PARKING],
        messages[PropertyType.EXCURSIONS],
      ],
    },
    valuation: {
      messages: {
        label: messages.valuation,
      },
      default: () => 1,
      enumLabels: [
        messages[PropertyValuation.ONE_STAR],
        messages[PropertyValuation.TWO_STAR],
        messages[PropertyValuation.THREE_STAR],
        messages[PropertyValuation.FOUR_STAR],
        messages[PropertyValuation.FIVE_STAR],
      ],
    },
    translationsList: {
      ...translationFieldConfig(allowedLocales || []),
      children: {
        __fieldOrder: ["name", "description", "*"],
        name: {
          messages: {
            label: messages.propertyTranslationsNameLabel,
          },
        },
        description: {
          widget: HtmlEditorWidget,
          messages: {
            label: messages.propertyTranslationsDescriptionLabel,
          },
        },
        ...translationFieldConfig(allowedLocales || []).children,
      },
      messages: {
        label: messages.propertyTranslationsTitle,
      },
    },
    locationSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.location,
      },
    },
    country: {
      widget: CountrySelector,
      messages: {
        label: messages.country,
      },
    },
    city: {
      messages: {
        label: messages.city,
      },
    },
    address: {
      messages: {
        label: messages.address,
      },
    },
    zipCode: {
      messages: {
        label: messages.zipCode,
      },
    },
    searchCoordinates: {
      fieldWidget: (props: FieldProps) => <GooglePlacesSelector {...props} addSubmitButton />,
      messages: {
        label: messages.searchCoordinates,
        helpText: messages.searchCoordinatesHelpText,
      },
    },
    latitude: {
      readonly: true,
      messages: {
        label: messages.latitude,
      },
    },
    longitude: {
      readonly: true,
      messages: {
        label: messages.longitude,
      },
    },
    timeZone: {
      readonly: true,
      messages: {
        label: messages.timeZone,
      },
    },
    phone: {
      messages: {
        label: messages.phone,
      },
    },
    adminEmail: {
      messages: {
        label: messages.adminEmail,
      },
    },
    contactEmail: {
      messages: {
        label: messages.contactEmail,
      },
    },
    contactSection: {
      fieldWidget: SectionUnderline,
      messages: {
        label: messages.contact,
      },
    },
    multimediaList: {
      ...imageManagerConfig({
        locales: allowedLocales,
        maxSizeMB: config.mediaMaxSizeMB,
        minWidth: config.mediaMinWidth,
        maxFiles:
          (formConfiguration?.propertyImageLimit &&
            Number(formConfiguration?.propertyImageLimit)) ||
          20,
        allowEdit: false,
        allowDeleteSelection: true,
        addBackgroundToPreview: true,
        formats: "png, jpeg, jpg",
        helpText: messages.imagesHelpText,
      }),
      messages: {
        label: messages.images,
      },
    },
  };
}
